import { ClockIcon, HeartIcon } from '@heroicons/react/24/outline'
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid'
import React, { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import apiService from '../api/apiService'
import { useAuth } from '../contexts/AuthContext'
import RecipeComments from './RecipeComments'
import ImageCarousel from './RecipeList/ImageCarousel.jsx'
import RecipeStructuredData from './RecipeStructuredData'
import SocialSharing from './SocialSharing'
import { Button } from './ui'
import CalendarDialog from './ui/CalendarDialog'

// Generate a very light pastel color with optional seed for deterministic generation
// eslint-disable-next-line no-unused-vars
const getRandomPastelColor = (seed) => {
  // If seed is provided, use it to generate a deterministic color
  let r, g, b

  if (seed) {
    // Create a simple hash from the seed
    const hash = String(seed)
      .split('')
      .reduce((acc, char) => {
        return char.charCodeAt(0) + ((acc << 5) - acc)
      }, 0)

    // Use the hash to generate very light pastel RGB values
    const h = Math.abs(hash) % 360 // Hue (0-360)
    const s = 25 + (Math.abs(hash) % 20) // Lower saturation (25-45%)
    const l = 88 + (Math.abs(hash) % 7) // Higher lightness (88-95%)

    // Convert HSL to RGB
    const c = (1 - Math.abs(2 * (l / 100) - 1)) * (s / 100)
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
    const m = l / 100 - c / 2

    if (h < 60) {
      ;[r, g, b] = [c, x, 0]
    } else if (h < 120) {
      ;[r, g, b] = [x, c, 0]
    } else if (h < 180) {
      ;[r, g, b] = [0, c, x]
    } else if (h < 240) {
      ;[r, g, b] = [0, x, c]
    } else if (h < 300) {
      ;[r, g, b] = [x, 0, c]
    } else {
      ;[r, g, b] = [c, 0, x]
    }

    r = Math.round((r + m) * 255)
      .toString(16)
      .padStart(2, '0')
    g = Math.round((g + m) * 255)
      .toString(16)
      .padStart(2, '0')
    b = Math.round((b + m) * 255)
      .toString(16)
      .padStart(2, '0')
  } else {
    // Random very light pastel color
    r = (Math.floor(Math.random() * 30) + 225).toString(16).padStart(2, '0')
    g = (Math.floor(Math.random() * 30) + 225).toString(16).padStart(2, '0')
    b = (Math.floor(Math.random() * 30) + 225).toString(16).padStart(2, '0')
  }

  return `${r}${g}${b}`
}

const RecipeDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [searchParams] = useSearchParams()
  const [recipe, setRecipe] = useState(null)
  const [loading, setLoading] = useState(true)
  const [dbUser, setDbUser] = useState(null)
  const [favorites, setFavorites] = useState(new Set())
  const [showCalendarDialog, setShowCalendarDialog] = useState(false)
  const [completedSteps, setCompletedSteps] = useState(new Set())
  const [completedIngredients, setCompletedIngredients] = useState(new Set())

  // Get highlighted comment ID from URL params
  const highlightedCommentId = searchParams.get('comment')

  useEffect(() => {
    const fetchRecipe = async () => {
      try {
        setLoading(true)
        const data = await apiService.getDetailedRecipe(id)
        setRecipe(data)
      } catch (error) {
        console.error('Error fetching recipe:', error)
      } finally {
        setLoading(false)
      }
    }

    if (id) {
      fetchRecipe()
    }
  }, [id])

  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) return

      try {
        // Fetch user data
        const userData = await apiService.getUserByEmail(user.email)
        setDbUser(userData)

        // Fetch favorites
        const favoritesData = await apiService.getFavorites(userData.id)
        const favoritesSet = new Set(favoritesData.map((fav) => fav.recipe_id))
        setFavorites(favoritesSet)
      } catch (err) {
        console.error('Error fetching user data:', err)
      }
    }

    fetchUserData()
  }, [user, id])

  const handleToggleFavorite = async (e) => {
    if (e) e.stopPropagation()
    if (!user || !dbUser) return

    try {
      const isFavorited = favorites.has(recipe.id)
      const action = isFavorited ? 'remove' : 'add'
      
      await apiService.toggleFavorite(dbUser.id, recipe.id, action)

      setFavorites((prev) => {
        const newFavorites = new Set(prev)
        if (isFavorited) {
          newFavorites.delete(recipe.id)
        } else {
          newFavorites.add(recipe.id)
        }
        return newFavorites
      })
    } catch (error) {
      console.error('Error toggling favorite:', error)
    }
  }

  const handleEdit = () => {
    if (!user || !dbUser) return
    navigate('/?edit=' + recipe.id)
  }

  const handleDelete = async () => {
    if (!user || !dbUser) return
    if (!window.confirm('Are you sure you want to delete this recipe?')) return

    try {
      await apiService.deleteRecipeWithUserId(recipe.id, dbUser.id)
      navigate('/')
    } catch (error) {
      console.error('Error deleting recipe:', error)
      alert('Failed to delete recipe: ' + error.message)
    }
  }

  const handleDeleteBrokenImage = async (imageUrl) => {
    if (!user || !dbUser) return

    try {
      await apiService.deleteBrokenRecipeImage(recipe.id, imageUrl, dbUser.id)
      
      // Update the recipe state to remove the broken image
      setRecipe(prev => ({
        ...prev,
        image_url: prev.image_url === imageUrl ? null : prev.image_url,
        recipe_images: prev.recipe_images?.filter(img => img.image_url !== imageUrl) || []
      }))
    } catch (error) {
      console.error('Error deleting broken image:', error)
      alert('Failed to delete broken image: ' + error.message)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="text-gray-600">Loading recipe...</div>
      </div>
    )
  }

  if (!recipe) {
    return (
      <div className="flex flex-col items-center justify-center py-8 px-3">
        <p className="text-gray-500 mb-3">Recipe not found</p>
        <Button variant="primary" onClick={() => navigate('/')}>
          Back to Recipes
        </Button>
      </div>
    )
  }

  // Get all available images
  const recipeImages = recipe.recipe_images || []

  // Get images sorted by display_order, same logic as RecipeCard
  const getAllImages = () => {
    if (recipeImages.length > 0) {
      // Sort images by display_order for proper prioritization, fallback to created_at
      const sortedImages = [...recipeImages].sort((a, b) => {
        // Primary sort by display_order (ascending - 1-based, so 1, 2, 3...)
        if (a.display_order !== b.display_order) {
          return (a.display_order || 999) - (b.display_order || 999)
        }
        // Secondary sort by created_at (newest first) if display_order is the same
        return new Date(b.created_at || 0) - new Date(a.created_at || 0)
      })

      return sortedImages
    }

    // Fallback to the legacy image_url if recipe_images isn't available
    if (recipe.image_url) {
      return [{ id: 'legacy', image_url: recipe.image_url, display_order: 1 }] // Use 1 instead of 0
    }

    return []
  }

  const allImages = getAllImages()
  const primaryImageUrl = allImages.length > 0 ? allImages[0]?.image_url : null
  const isOwner = dbUser && recipe.user_id === dbUser.id
  const isFavorited = favorites.has(parseInt(id))

  // Parse ingredients
  let parsedIngredients = []
  try {
    // First try to parse as JSON
    parsedIngredients = JSON.parse(recipe.ingredients || '[]')
  } catch (error) {
    try {
      // Try to parse as string format
      const ingredientString = recipe.ingredients || ''
      // Remove the outer brackets
      const cleanString = ingredientString.replace(/^\[|\]$/g, '')
      // Split by "},{"
      parsedIngredients = cleanString.split('},{').map((item) => {
        // Clean up the item string and parse as JSON
        const cleanItem = item.replace(/^{/, '{').replace(/}$/, '}')
        return JSON.parse(cleanItem)
      })
    } catch (e) {
      // If all parsing fails, fall back to simple string split
      parsedIngredients = (recipe.ingredients || '')
        .split(',')
        .map((ingredient) => ({ item: ingredient.trim() }))
    }
  }

  // Parse instructions
  let parsedInstructions = []
  try {
    // Check if instructions is already an array
    if (Array.isArray(recipe.instructions)) {
      parsedInstructions = recipe.instructions
    } else {
      // First try to parse as JSON
      parsedInstructions = JSON.parse(recipe.instructions || '[]')
    }
  } catch (error) {
    // If parsing fails, fall back to simple string split
    parsedInstructions = (recipe.instructions || '')
      .split(',')
      .map((instruction) => instruction.trim())
      .filter((instruction) => instruction.length > 0)
  }

  return (
    <div className="max-w-2xl md:max-w-5xl mx-auto pt-2 px-3 pb-4">
      <RecipeStructuredData recipe={recipe} />

      {/* Header with title, time and action buttons on same row (desktop) */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between py-3">
        <div className="flex flex-col">
          <h1 className="text-xl md:text-2xl font-semibold text-gray-800 tracking-tight mb-1">
            {recipe.name}
          </h1>
        </div>

        <div className="flex flex-wrap items-center mt-2 md:mt-0 gap-y-2 gap-x-2">
          {/* Cooking time */}
          {recipe.cooking_time_minutes && (
            <div className="flex items-center bg-white shadow-sm px-2.5 py-1 rounded-full border border-gray-100">
              <ClockIcon className="h-4 w-4 text-green-500 mr-1" />
              <span className="text-xs font-medium">
                {recipe.cooking_time_minutes} min
              </span>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-1 items-center">
            {user && (
              <button
                onClick={handleToggleFavorite}
                className="p-1.5 bg-white rounded-full shadow-sm border border-gray-100 hover:shadow transition-all duration-150 group"
              >
                {isFavorited ? (
                  <HeartIconSolid className="h-5 w-5 text-red-500 group-hover:scale-110 transition-transform duration-150" />
                ) : (
                  <HeartIcon className="h-5 w-5 text-red-400 group-hover:scale-110 transition-transform duration-150" />
                )}
              </button>
            )}

            <button className="p-1.5 bg-white rounded-full shadow-sm border border-gray-100 hover:bg-green-50 hover:border-green-200 hover:shadow transition-all duration-150 group">
              <SocialSharing recipe={recipe} recipeId={recipe.id} />
            </button>
          </div>
        </div>
      </div>

      {/* Recipe source link */}
      <div className="flex flex-wrap items-center mb-3 gap-y-2 gap-x-2">
        {recipe.url && (
          <a
            href={recipe.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-green-600 hover:text-green-700 transition-colors duration-150 text-xs font-medium flex items-center bg-white shadow-sm px-2.5 py-1 rounded-full border border-gray-100"
          >
            <span className="mr-1">Source</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-3 w-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
          </a>
        )}
      </div>

      {/* Main content with desktop columns - compact styling */}
      <div className="space-y-4 md:space-y-0 md:flex md:gap-5 md:items-start">
        {/* Left column: Image + Ingredients */}
        <div className="md:w-2/5 space-y-4">
          {/* Image carousel - beautiful presentation */}
          {allImages.length > 0 && (
            <div className="relative rounded-lg overflow-hidden shadow-lg transform transition-all duration-300 hover:translate-y-[-2px]">
              <ImageCarousel
                images={allImages.map((img) => img.image_url)}
                alt={recipe.name}
                className="w-full h-56 md:h-[320px] lg:h-[350px] overflow-hidden"
              />
              <div className="absolute inset-0 rounded-lg ring-1 ring-black ring-opacity-5 pointer-events-none"></div>
            </div>
          )}

          {/* Ingredients section - elegant styling */}
          <div className="bg-white rounded-lg border border-gray-100 shadow-md overflow-hidden self-start transform transition-all duration-300 hover:translate-y-[-2px]">
            <div className="px-3 py-2 bg-gradient-to-r from-green-50 to-green-100 border-b border-green-100 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-green-800 flex items-center">
                Ingredients
              </h2>
              <span className="text-xs font-medium text-green-700 bg-white bg-opacity-60 px-2 py-0.5 rounded-full shadow-sm">
                {parsedIngredients.length} items
              </span>
            </div>

            <div className="p-2">
              <div className="divide-y divide-gray-100">
                {parsedIngredients.map((ingredient, index) => {
                  // Define units that should have no space
                  const noSpaceUnits = [
                    'g',
                    'kg',
                    'mL',
                    'L',
                    'tsp',
                    'tbsp',
                    'oz',
                    'lb',
                  ]
                  const hasNoSpace = noSpaceUnits.includes(ingredient.unit)
                  const isCompleted = completedIngredients.has(index)

                  return (
                    <div
                      key={index}
                      className="py-1.5 flex items-center justify-between group hover:bg-gray-50 transition-colors duration-150 cursor-pointer"
                      onClick={() => {
                        setCompletedIngredients((prev) => {
                          const newSet = new Set(prev)
                          if (newSet.has(index)) {
                            newSet.delete(index)
                          } else {
                            newSet.add(index)
                          }
                          return newSet
                        })
                      }}
                    >
                      <div className="flex items-center">
                        <div
                          className={`w-3 h-3 rounded-full mr-1.5 flex-shrink-0 flex items-center justify-center transition-colors duration-150 ${
                            isCompleted
                              ? 'bg-green-100 border border-green-400'
                              : 'bg-green-200 border border-green-300'
                          }`}
                        >
                          {isCompleted && (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-2.5 w-2.5 text-green-600"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                        </div>
                        <span
                          className={`text-sm font-medium ${isCompleted ? 'text-gray-500 line-through' : 'text-gray-800'}`}
                        >
                          {ingredient.item}
                        </span>
                      </div>
                      {ingredient.amount && (
                        <span
                          className={`text-xs font-medium tabular-nums bg-gray-50 px-1.5 py-0.5 rounded transition-colors duration-150 ${
                            isCompleted ? 'text-gray-400' : 'text-gray-600'
                          }`}
                        >
                          {ingredient.amount}
                          {ingredient.unit &&
                            (hasNoSpace
                              ? ingredient.unit
                              : ` ${ingredient.unit}`)}
                        </span>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Right column: Instructions */}
        <div className="md:w-3/5">
          <div className="bg-white rounded-lg border border-gray-100 shadow-md overflow-hidden self-start transform transition-all duration-300 hover:translate-y-[-2px]">
            <div className="px-3 py-2 bg-gradient-to-r from-blue-50 to-blue-100 border-b border-blue-100 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-blue-800 flex items-center">
                Instructions
              </h2>
              <span className="text-xs font-medium text-blue-700 bg-white bg-opacity-60 px-2 py-0.5 rounded-full shadow-sm">
                {parsedInstructions.length} steps
              </span>
            </div>

            <div className="p-3">
              <ol className="list-none space-y-3">
                {parsedInstructions.map((instruction, index) => {
                  const isCompleted = completedSteps.has(index)

                  return (
                    <li
                      key={index}
                      className="flex group cursor-pointer"
                      onClick={() => {
                        // Toggle completion state
                        setCompletedSteps((prev) => {
                          const newSet = new Set(prev)
                          if (newSet.has(index)) {
                            newSet.delete(index)
                          } else {
                            newSet.add(index)
                          }
                          return newSet
                        })
                      }}
                    >
                      <div
                        className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center mr-2 font-medium text-sm shadow-sm transition-all duration-150 ${
                          isCompleted
                            ? 'bg-green-100 text-green-700 group-hover:bg-green-200'
                            : 'bg-blue-100 text-blue-800 group-hover:bg-blue-200'
                        }`}
                      >
                        {isCompleted ? (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-3.5 w-3.5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        ) : (
                          index + 1
                        )}
                      </div>
                      <span
                        className={`text-sm leading-relaxed ${
                          isCompleted
                            ? 'text-gray-500 line-through'
                            : 'text-gray-700'
                        }`}
                      >
                        {instruction}
                      </span>
                    </li>
                  )
                })}
              </ol>
            </div>
          </div>
        </div>
      </div>

      {/* Calendar Dialog */}
      {showCalendarDialog && (
        <CalendarDialog
          recipe={{
            id: recipe.id,
            name: recipe.name,
            image_url: primaryImageUrl || undefined,
          }}
          onClose={() => setShowCalendarDialog(false)}
        />
      )}

      {/* Recipe Comments Section */}
      <div className="mt-8 border-t border-gray-200 pt-6">
        <RecipeComments
          recipeId={recipe.id}
          recipeName={recipe.name}
          highlightedCommentId={highlightedCommentId}
        />
      </div>
    </div>
  )
}

export default RecipeDetail
